<template>
  <div class="form-page-container">
    <PremiumPage
      :title="isEdit ? 'Edit Poll' : 'Create New Poll'"
      subtitle="Configure poll settings, questions, and options."
      showBack
      show-footer-buttons
      :submit-label="isEdit ? 'Update Poll' : 'Save Poll'"
      :loading="saving"
      @cancel="$router.push('/societyadmin/poll')"
      @submit="savePoll"
    >
      <div class="half-width-wrap">

        <!-- ── Section 1: Basic Information ── -->
        <div class="ap-section">
          <div class="ap-section-hdr" :style="sectionHdrStyle">
            <span class="ap-section-title">
              <v-icon small :color="primaryColor" class="mr-2">mdi-help-circle-outline</v-icon>
              Basic Information
            </span>
          </div>
          <div class="ap-section-body">
            <v-row>
              <v-col cols="12" md="8">
                <PremiumInput
                  v-model="poll.title"
                  label="Poll Question / Title"
                  placeholder="E.g. Which paint color for the lobby?"
                  icon="mdi-help-circle-outline"
                  :error="errors.title"
                  @input="errors.title = ''"
                />
              </v-col>
              <v-col cols="12" md="4">
                <PremiumSelect
                  v-model="poll.type"
                  label="Poll Type"
                  icon="mdi-format-list-bulleted-type"
                  :items="pollTypes"
                  item-text="label"
                  item-value="value"
                  placeholder="Select poll type"
                  :disabled="isEdit"
                  @input="onTypeChange"
                />
              </v-col>
              <v-col cols="12">
                <PremiumInput
                  v-model="poll.description"
                  label="Description (Optional)"
                  placeholder="Additional instructions or context for voters"
                  multiline
                  rows="3"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="ap-section-divider"></div>

        <!-- ── Section 2: Answer Options ── -->
        <div class="ap-section">
          <div class="ap-section-hdr" :style="sectionHdrStyle">
            <span class="ap-section-title">
              <v-icon small :color="primaryColor" class="mr-2">mdi-format-list-checks</v-icon>
              Answer Options
            </span>
          </div>
          <div class="ap-section-body">
            <div v-if="poll.type === 'single_choice' || poll.type === 'multiple_choice'">
              <v-row>
                <v-col v-for="(opt, index) in poll.options" :key="index" cols="12" sm="6">
                  <div class="d-flex align-center">
                    <PremiumInput
                      v-model="opt.title"
                      :label="`Option ${index + 1}`"
                      :placeholder="`Enter option ${index + 1}`"
                      icon="mdi-circle-small"
                      class="flex-grow-1"
                    />
                    <v-tooltip top :open-delay="200" content-class="action-tooltip" :disabled="canDeleteOption">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-on="on" class="ml-1 mt-4">
                          <v-btn icon color="error" @click="removeOption(index)" :disabled="!canDeleteOption" v-bind="attrs">
                            <v-icon size="20">mdi-delete-outline</v-icon>
                          </v-btn>
                        </span>
                      </template>
                      <span v-if="hasVotes">Votes exist — options cannot be deleted</span>
                      <span v-else>Minimum 2 options required</span>
                    </v-tooltip>
                  </div>
                </v-col>
              </v-row>
              <div v-if="optionsError" class="options-error-msg mt-1">{{ optionsError }}</div>
              <v-btn text small color="var(--brand)" @click="addOption" class="mt-2 pl-0">
                <v-icon left size="16">mdi-plus</v-icon> Add Option
              </v-btn>
            </div>
            <div v-else class="no-options-box">
              <v-icon size="20" color="#94A3B8" class="mr-2">mdi-information-outline</v-icon>
              <span>No predefined options needed for {{ poll.type === 'text' ? 'Text' : 'Number' }} answers.</span>
            </div>
          </div>
        </div>

        <div class="ap-section-divider"></div>

        <!-- ── Section 3: Schedule ── -->
        <div class="ap-section">
          <div class="ap-section-hdr" :style="sectionHdrStyle">
            <span class="ap-section-title">
              <v-icon small :color="primaryColor" class="mr-2">mdi-calendar-clock</v-icon>
              Schedule
            </span>
          </div>
          <div class="ap-section-body">
            <v-row>
              <v-col cols="12" sm="4">
                <PremiumDateTimePicker
                  v-model="poll.start_date"
                  label="Start Date & Time"
                  placeholder="Select start date & time"
                  :min="currentDateString"
                  :error="errors.start_date"
                  @input="errors.start_date = ''"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <PremiumDateTimePicker
                  v-model="poll.expiry_date"
                  label="Expiry Date & Time"
                  placeholder="Select expiry date & time"
                  :min="currentDateString"
                  :error="errors.expiry_date"
                  @input="errors.expiry_date = ''"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <PremiumDateTimePicker
                  v-model="poll.auto_delete_date"
                  label="Auto Delete Date"
                  placeholder="Select auto-delete date"
                  :min="currentDateString"
                  :error="errors.auto_delete_date"
                  @input="errors.auto_delete_date = ''"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="ap-section-divider"></div>

        <!-- ── Section 4: Voting Settings ── -->
        <div class="ap-section">
          <div class="ap-section-hdr" :style="sectionHdrStyle">
            <span class="ap-section-title">
              <v-icon small :color="primaryColor" class="mr-2">mdi-shield-check-outline</v-icon>
              Voting Settings
            </span>
          </div>
          <div class="ap-section-body">
            <v-row align="center">
              <v-col cols="12" sm="4">
                <PremiumSelect
                  v-model="poll.show_results"
                  label="Result Visibility"
                  icon="mdi-eye-outline"
                  :items="resultSettings"
                  item-text="label"
                  item-value="value"
                  placeholder="When to show results"
                />
              </v-col>
              <v-col cols="12" sm="8">
                <div class="voting-switches-row">
                  <div class="vs-item">
                    <div class="vs-info">
                      <div class="vs-label">Anonymous Voting</div>
                      <div class="vs-hint">Hide voter identity from results</div>
                    </div>
                    <v-switch v-model="poll.is_anonymous" dense hide-details color="var(--brand)" class="vs-switch" />
                  </div>
                  <div class="vs-sep"></div>
                  <div class="vs-item">
                    <div class="vs-info">
                      <div class="vs-label">Allow Vote Change</div>
                      <div class="vs-hint">Members can modify their vote until expiry</div>
                    </div>
                    <v-switch v-model="poll.allow_vote_change" dense hide-details color="var(--brand)" class="vs-switch" />
                  </div>
                  <div class="vs-sep"></div>
                  <div class="vs-item">
                    <div class="vs-info">
                      <div class="vs-label">One Vote Per Unit</div>
                      <div class="vs-hint">Restrict to one response per flat/unit</div>
                    </div>
                    <v-switch v-model="poll.one_vote_per_unit" dense hide-details color="var(--brand)" class="vs-switch" />
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </div>

      </div>
    </PremiumPage>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import PremiumPage from "../../common/PremiumPage.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import PremiumSelect from "../../common/PremiumSelect.vue";
import PremiumDateTimePicker from "../../common/PremiumDateTimePicker.vue";

export default {
  name: "AddPoll",
  components: { PremiumPage, PremiumInput, PremiumSelect, PremiumDateTimePicker },

  data() {
    return {
      isEdit: false,
      saving: false,
      hasVotes: false,
      errors: {
        title: '',
        start_date: '',
        expiry_date: '',
        auto_delete_date: '',
      },
      optionsError: '',
      poll: {
        title: "",
        description: "",
        type: "single_choice",
        start_date: null,
        expiry_date: null,
        auto_delete_date: null,
        options: [{ title: "" }, { title: "" }],
        is_anonymous: false,
        allow_vote_change: false,
        show_results: "after_vote",
        one_vote_per_unit: false,
      },
      pollTypes: [
        { label: "Single Choice",   value: "single_choice" },
        { label: "Multiple Choice", value: "multiple_choice" },
        { label: "Text Answer",     value: "text" },
        { label: "Number Answer",   value: "number" },
      ],
      resultSettings: [
        { label: "Never",           value: "never" },
        { label: "After Voting",    value: "after_vote" },
        { label: "After Poll Ends", value: "after_poll_ends" },
        { label: "Always",          value: "always" },
      ],
    };
  },

  computed: {
    ...mapState("theme", ["primaryColor"]),
    canDeleteOption() {
      return !this.hasVotes && this.poll.options.length > 2;
    },
    currentDateString() {
      return new Date().toISOString().substring(0, 10);
    },
    sectionHdrStyle() {
      return {};
    },
    sectionIconStyle() {
      return {};
    },
  },

  created() {
    if (this.$route.params.id) {
      this.isEdit = true;
      this.fetchPoll();
    } else {
      const now = new Date();
      this.poll.start_date = now;
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.poll.expiry_date = tomorrow;
    }
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    async fetchPoll() {
      try {
        const res = await this.$axios.get(`/societyadmin/polls/${this.$route.params.id}`);
        if (res.data.status) {
          const data = res.data.data;
          this.poll = {
            ...data,
            start_date:       data.start_date       ? new Date(data.start_date)       : null,
            expiry_date:      data.expiry_date       ? new Date(data.expiry_date)      : null,
            auto_delete_date: data.auto_delete_date  ? new Date(data.auto_delete_date) : null,
            options: data.options && data.options.length ? data.options : [{ title: "" }, { title: "" }],
          };
          if (data.live_results && data.live_results.total_votes > 0) {
            this.hasVotes = true;
          }
        } else {
          this.showError("Failed to load poll details");
          this.$router.push("/societyadmin/poll");
        }
      } catch (err) {
        this.showError("Error loading poll");
      }
    },

    onTypeChange(val) {
      if (val === "single_choice" || val === "multiple_choice") {
        if (this.poll.options.length < 2) {
          this.poll.options = [{ title: "" }, { title: "" }];
        }
      } else {
        this.poll.options = [];
      }
    },

    addOption() {
      this.poll.options.push({ title: "" });
    },

    removeOption(index) {
      if (this.poll.options.length > 2) {
        this.poll.options.splice(index, 1);
      }
    },

    async savePoll() {
      this.errors = { title: '', start_date: '', expiry_date: '', auto_delete_date: '' };
      let hasError = false;

      if (!this.poll.title || !this.poll.title.trim()) { this.errors.title = 'Title is required'; hasError = true; }
      if (!this.poll.start_date) { this.errors.start_date = 'Start date is required'; hasError = true; }
      if (!this.poll.expiry_date) { this.errors.expiry_date = 'Expiry date is required'; hasError = true; }
      if (!this.poll.auto_delete_date) { this.errors.auto_delete_date = 'Auto Delete date is required'; hasError = true; }
      if (!hasError && new Date(this.poll.expiry_date) <= new Date(this.poll.start_date)) {
        this.errors.expiry_date = 'Expiry date must be after Start date'; hasError = true;
      }
      if (!hasError && new Date(this.poll.auto_delete_date) <= new Date(this.poll.expiry_date)) {
        this.errors.auto_delete_date = 'Auto Delete date must be after Expiry date'; hasError = true;
      }
      if (hasError) return;

      if (this.poll.type === "single_choice" || this.poll.type === "multiple_choice") {
        const validOptions = this.poll.options.filter(o => o.title && o.title.trim() !== "");
        if (validOptions.length < 2) { this.optionsError = 'At least two valid options are required'; return; }
        this.optionsError = '';
        this.poll.options = validOptions;
      }

      this.saving = true;
      try {
        let res;
        if (this.isEdit) {
          res = await this.$axios.put(`/societyadmin/polls/${this.$route.params.id}`, this.poll);
        } else {
          res = await this.$axios.post("/societyadmin/polls", this.poll);
        }
        if (res.data.status) {
          this.setAlert({ showAlert: true, message: res.data.message, variant: "green accent-4", timeout: 2000 });
          this.$router.push("/societyadmin/poll");
        } else {
          this.showError(res.data.message || "Failed to save poll");
        }
      } catch (err) {
        this.showError(err.response?.data?.message || "An error occurred");
      } finally {
        this.saving = false;
      }
    },

    showError(msg) {
      this.setAlert({ showAlert: true, message: msg, variant: "red accent-3", timeout: 2500 });
    },
  },
};
</script>

<style scoped>
.form-page-container {
  padding: 24px;
  width: 100%;
  margin: 0 auto;
}
.half-width-wrap {
  max-width: 70%;
}
@media (max-width: 960px) {
  .half-width-wrap {
    max-width: 100%;
  }
}

/* ── Section ── */
.ap-section {
  padding: 16px;
}

/* ── Section Divider ── */
.ap-section-divider {
  height: 1px;
  background: var(--border, #e2e8f0);
  opacity: 0.6;
  margin: 0 20px;
}

/* ── Section Header ── */
.ap-section-hdr {
  display: flex;
  align-items: center;
  margin-bottom: 22px;
}
.ap-section-icon {
  display: none;
}
.ap-section-title {
  font-size: 13.5px;
  font-weight: 800;
  color: var(--tx, #111827);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
}

/* ── Section Body ── */
.ap-section-body {
  padding-bottom: 0;
}

/* ── No Options Box ── */
.no-options-box {
  display: flex;
  align-items: center;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 13px;
  color: var(--tx3, #64748b);
  font-weight: 500;
}

/* ── Voting Switches ── */
.voting-switches-row {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 10px;
  overflow: hidden;
}
.vs-item {
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  gap: 8px;
}
.vs-info { flex: 1; min-width: 0; }
.vs-switch { flex-shrink: 0; margin: 0 !important; }
.vs-sep {
  width: 1px;
  background: var(--border, #e2e8f0);
  align-self: stretch;
}
.vs-label {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tx, #1e293b);
  line-height: 1.3;
}
.vs-hint {
  font-size: 11px;
  color: var(--tx3, #94a3b8);
  margin-top: 2px;
  line-height: 1.3;
}

.options-error-msg {
  font-size: 12px;
  color: #EF4444;
  font-weight: 500;
}
</style>

<style>
/* ── Delete icon dark mode fix (global — scoped cannot reach theme--dark on html) ── */
.theme--dark .v-btn--icon.v-btn--disabled {
  opacity: 1 !important;
}
.theme--dark .v-btn--icon.v-btn--disabled .v-icon {
  color: #64748b !important;
}
.theme--dark .v-btn--icon:not(.v-btn--disabled) .v-icon {
  color: #f87171 !important;
}

.action-tooltip {
  background: #111827 !important;
  color: #fff !important;
  font-size: 11.5px !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
  padding: 4px 8px !important;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06);
}
</style>
